import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import cn from 'classnames';
import { Badge, Divider, IconButton, TextButton, Typography, useDevice, useOverlayContext } from 'seagull';
import { ChevronLeft, ChevronRight, EditAI } from 'seagull/icons';
import { generateUniqueId } from 'seagull/utils/generateUniqueId';
import { AiGradientShell } from '~/components/ai/AiGradientShell/AiGradientShell';
import { MAX_FILE_SIZE_BYTES } from '~/components/ImportAgreementModal/ImportAgreementModal.utils';
import { useIoContext, useStoreActions } from '~/contexts';
import { useFeatureFlags } from '~/hooks/useFeatureFlags';
import { useLocaleKeys } from '~/i18n';
import { handleRequest } from '~/infra/utils';
import { sleepWithAbort } from '~/utils/sleepWithAbort';
import styles from './AgentDraftingPane.module.scss';
import { GetAgentDraftingPaneTID, POLL_INTERVAL_MS, POLL_MAX_ATTEMPTS, resolveNumberedListSelection, SOMETHING_ELSE_OPTION_ID } from './AgentDraftingPane.utils';
import { AgentChatOpenState } from './open-state/AgentChatOpenState';
const initialChatState = {
    messages: [],
    isThinking: false,
    isUploading: false,
    uploadingFileName: null,
    pendingAttachment: null,
    activeToolCalls: [],
    thinkingText: null,
    thinkingLabel: null,
    error: null
};
const readConversationIdFromLocationState = (state)=>{
    if (state == null || typeof state !== 'object') return null;
    const value = state.conversationId;
    return typeof value === 'string' && value.length > 0 ? value : null;
};
const describeError = (err)=>err instanceof Error ? err.message : String(err);
export const AgentDraftingPane = (param)=>{
    let { onDraftCreated, onTurnApplied, testId = '', isOpen, onOpenChange, pollIntervalMs, draftId, isAutoSaving } = param;
    const { HELP_ME_DRAFT } = useFeatureFlags();
    const { isMobileDevice } = useDevice();
    if (!HELP_ME_DRAFT || isMobileDevice) return null;
    return /*#__PURE__*/ _jsx(AgentDraftingPaneImpl, {
        onDraftCreated: onDraftCreated,
        onTurnApplied: onTurnApplied,
        testId: testId,
        isOpen: isOpen,
        onOpenChange: onOpenChange,
        pollIntervalMs: pollIntervalMs,
        draftId: draftId,
        isAutoSaving: isAutoSaving
    });
};
const AgentDraftingPaneImpl = (param)=>{
    let { onDraftCreated, onTurnApplied, testId, isOpen: controlledIsOpen, onOpenChange, pollIntervalMs = POLL_INTERVAL_MS, draftId, isAutoSaving } = param;
    const location = useLocation();
    const initialConversationId = readConversationIdFromLocationState(location.state);
    const [internalIsOpen, setInternalIsOpen] = useState(draftId == null || initialConversationId != null);
    const isControlled = controlledIsOpen != null;
    const isOpen = isControlled ? controlledIsOpen : internalIsOpen;
    const setIsOpen = (open)=>{
        if (!isControlled) setInternalIsOpen(open);
        onOpenChange === null || onOpenChange === void 0 ? void 0 : onOpenChange(open);
    };
    const [chat, setChat] = useState(initialChatState);
    const [isBodyScrolled, setIsBodyScrolled] = useState(false);
    useEffect(()=>{
        if (!isOpen) setIsBodyScrolled(false);
    }, [
        isOpen
    ]);
    const { t } = useLocaleKeys();
    const { AgentDraftingPaneTestIds } = useMemo(()=>GetAgentDraftingPaneTID(testId), [
        testId
    ]);
    const isDebug = useMemo(()=>new URLSearchParams(location.search).get('debug') === 'true', [
        location.search
    ]);
    const { api } = useStoreActions();
    const { intercomClient, browser: { uploadFileToCloudStorage } } = useIoContext();
    useEffect(()=>{
        if (isOpen) {
            intercomClient.hideIntercom();
        } else {
            intercomClient.showIntercom();
        }
    }, [
        isOpen
    ]);
    const { showErrorToast, showSuccessToast } = useOverlayContext();
    const conversationIdRef = useRef(initialConversationId);
    const createInFlightRef = useRef(null);
    const abortRef = useRef(null);
    const turnInFlightRef = useRef(false);
    const attachedDraftIdRef = useRef(null);
    const clearEpochRef = useRef(0);
    useEffect(()=>()=>{
            var _abortRef_current;
            return (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        }, []);
    // Maps a backend error code (ApiErrorCode enum in node-services/ai-service) to a user-facing
    // string. Unknown codes fall back to the generic "something went wrong" copy.
    const resolveErrorMessage = (errorCode)=>{
        const codeTexts = t.agenticDrafting.errors.codes;
        switch(errorCode){
            case 'apply_not_implemented_in_this_phase':
                return codeTexts.applyNotImplementedInThisPhase();
            case 'conversation_not_found':
                return codeTexts.conversationNotFound();
            // Retry-unsafe: the PATCH may have committed server-side, so the UI must
            // not show "Try again" copy. See ApiErrorCode.DraftUpdateFailed in
            // node-services/ai-service/src/agentic-drafting-actor/handlers/api-errors.ts.
            case 'draft_update_failed':
                return codeTexts.draftUpdateFailed();
            case 'fragment_not_valid_for_update':
                return codeTexts.fragmentNotValidForUpdate();
            case 'internal_error':
                return codeTexts.internalError();
            case 'invalid_content':
                return codeTexts.invalidContent();
            case 'invalid_conversation_id':
                return codeTexts.invalidConversationId();
            case 'llm_error':
                return codeTexts.llmError();
            // Surfaced when the dispatcher receives a mix of `addService` and
            // `addPackage` fragments in the same apply call. mgmt-api invariant:
            // a draft body has packages OR services, never both.
            case 'mixed_packages_and_services':
                return codeTexts.mixedPackagesAndServices();
            case 'not_implemented_in_this_phase':
                return codeTexts.notImplementedInThisPhase();
            case 'pdf_read_failed':
                return codeTexts.pdfReadFailed();
            case 'turn_in_progress':
                return codeTexts.turnInProgress();
            default:
                return codeTexts.fallback();
        }
    };
    const onHydrate = (messages)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                messages
            }));
    const onBeginPolling = ()=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: true,
                activeToolCalls: [],
                thinkingText: null
            }));
    const onStreamError = (error)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                error,
                isThinking: false,
                activeToolCalls: [],
                thinkingText: null
            }));
    const onStreamDone = ()=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: false,
                activeToolCalls: [],
                thinkingText: null
            }));
    // Appends the server invite message during conversation-create reconcile without flipping
    // isThinking/activeToolCalls/thinkingText — the calling turn is still in flight and must
    // continue to show its thinking state.
    const onReconcileInvite = (message)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                messages: [
                    ...prev.messages,
                    message
                ]
            }));
    const pollUntilIdle = async (id, signal)=>{
        for(let attempt = 0; attempt < POLL_MAX_ATTEMPTS; attempt++){
            if (signal.aborted) return;
            try {
                const conversation = await api.agenticDraft.get(id);
                if (signal.aborted) return;
                if (!conversation.isTurnInFlight()) {
                    onHydrate(conversation.messages);
                    onStreamDone();
                    return;
                }
            } catch (err) {
                if (attempt === POLL_MAX_ATTEMPTS - 1) {
                    onStreamError(describeError(err));
                    return;
                }
            }
            try {
                await sleepWithAbort(pollIntervalMs, signal);
            } catch (unused) {
                return;
            }
        }
        onStreamError(t.agenticDrafting.errors.recoveryTimeout());
    };
    useEffect(()=>{
        const id = conversationIdRef.current;
        if (id == null) return;
        const controller = new AbortController();
        abortRef.current = controller;
        let cancelled = false;
        (async ()=>{
            try {
                const conversation = await api.agenticDraft.get(id);
                if (cancelled || controller.signal.aborted) return;
                onHydrate(conversation.messages);
                if (conversation.isTurnInFlight()) {
                    onBeginPolling();
                    await pollUntilIdle(id, controller.signal);
                }
            } catch (unused) {
                if (cancelled || controller.signal.aborted) return;
                showErrorToast(t.agenticDrafting.errors.hydrate());
            }
        })();
        return ()=>{
            cancelled = true;
            controller.abort();
        };
    }, []);
    const ensureConversationId = function ensureConversationId() {
        let options = arguments.length > 0 && arguments[0] !== void 0 ? arguments[0] : {};
        if (conversationIdRef.current != null) return Promise.resolve(conversationIdRef.current);
        if (createInFlightRef.current != null) return createInFlightRef.current;
        const isCreateForDraft = draftId != null;
        const epochAtCall = clearEpochRef.current;
        const forceFresh = options.forceFresh === true;
        const commitConversationId = (conversationId)=>{
            var _window_history_state;
            if (clearEpochRef.current !== epochAtCall) return conversationId;
            conversationIdRef.current = conversationId;
            if (isCreateForDraft) attachedDraftIdRef.current = draftId;
            const prevState = typeof location.state === 'object' && location.state != null ? location.state : {};
            // AgreementStudioProposalMode bypasses react-router via window.history.replaceState; matching that here keeps the draft URL from being clobbered on Clear Chat.
            const currentWindowState = (_window_history_state = window.history.state) !== null && _window_history_state !== void 0 ? _window_history_state : {};
            window.history.replaceState(_object_spread_props(_object_spread({}, currentWindowState), {
                state: _object_spread_props(_object_spread({}, prevState), {
                    conversationId
                })
            }), '', "".concat(window.location.pathname).concat(window.location.search));
            return conversationId;
        };
        const createFresh = ()=>(isCreateForDraft ? api.agenticDraft.createForDraft(draftId) : api.agenticDraft.create()).then((param)=>{
                let { conversationId, message } = param;
                const committed = commitConversationId(conversationId);
                if (clearEpochRef.current === epochAtCall) onReconcileInvite(message);
                return committed;
            });
        const pending = (isCreateForDraft && !forceFresh ? api.agenticDraft.findByDraft(draftId).then((existing)=>{
            if (existing == null) return createFresh();
            const committed = commitConversationId(existing.conversationId);
            if (clearEpochRef.current === epochAtCall) onHydrate(existing.messages);
            return committed;
        }) : createFresh()).finally(()=>{
            createInFlightRef.current = null;
        });
        createInFlightRef.current = pending;
        return pending;
    };
    const onClearChat = async ()=>{
        var _abortRef_current;
        (_abortRef_current = abortRef.current) === null || _abortRef_current === void 0 ? void 0 : _abortRef_current.abort();
        clearEpochRef.current += 1;
        conversationIdRef.current = null;
        attachedDraftIdRef.current = null;
        turnInFlightRef.current = false;
        createInFlightRef.current = null;
        setChat(_object_spread_props(_object_spread({}, initialChatState), {
            isThinking: true,
            thinkingLabel: t.agenticDrafting.body.clearing()
        }));
        const result = await handleRequest(ensureConversationId({
            forceFresh: true
        }));
        if (!result.success) showErrorToast(t.agenticDrafting.errors.clear());
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: false,
                thinkingLabel: null
            }));
    };
    const hasUserMessage = chat.messages.some((m)=>m.role === 'user');
    const isClearDisabled = !hasUserMessage || chat.thinkingLabel != null;
    const placeholderPool = useMemo(()=>{
        if (hasUserMessage) return [];
        const set = draftId == null ? t.agenticDrafting.input.placeholderEmpty : t.agenticDrafting.input.placeholderExisting;
        return [
            set.p1(),
            set.p2(),
            set.p3(),
            set.p4()
        ];
    }, [
        draftId,
        hasUserMessage,
        t
    ]);
    useEffect(()=>{
        if (!isOpen) return;
        if (conversationIdRef.current != null) return;
        if (createInFlightRef.current != null) return;
        ensureConversationId().catch(()=>undefined);
    }, [
        isOpen
    ]);
    useEffect(()=>{
        if (draftId == null) return;
        const conversationId = conversationIdRef.current;
        if (conversationId == null) return;
        if (attachedDraftIdRef.current === draftId) return;
        let cancelled = false;
        (async ()=>{
            const result = await handleRequest(api.agenticDraft.attachDraft(conversationId, draftId));
            if (cancelled || !result.success) return;
            attachedDraftIdRef.current = draftId;
        })();
        return ()=>{
            cancelled = true;
        };
    }, [
        draftId
    ]);
    const handleStreamEvent = (event)=>{
        // eslint-disable-next-line no-console
        if (isDebug) console.log('[AgentDraftingPane] stream event', JSON.stringify(event));
        return event.match({
            toolCallStart: (tool, args)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        activeToolCalls: [
                            ...prev.activeToolCalls,
                            {
                                tool,
                                args,
                                startedAt: Date.now()
                            }
                        ]
                    })),
            toolCallEnd: (tool, durationMs)=>setChat((prev)=>{
                    const idx = prev.activeToolCalls.findIndex((tc)=>tc.tool === tool && tc.durationMs == null);
                    if (idx === -1) return prev;
                    const next = [
                        ...prev.activeToolCalls
                    ];
                    next[idx] = _object_spread_props(_object_spread({}, next[idx]), {
                        durationMs
                    });
                    return _object_spread_props(_object_spread({}, prev), {
                        activeToolCalls: next
                    });
                }),
            thinking: (text)=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        thinkingText: text
                    })),
            assistantMessage: (message)=>{
                setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        messages: [
                            ...prev.messages,
                            message
                        ],
                        activeToolCalls: [],
                        thinkingText: null,
                        isThinking: false
                    }));
                if (message.content.type === 'applied') {
                    var _message_content_changedTargets;
                    onTurnApplied === null || onTurnApplied === void 0 ? void 0 : onTurnApplied((_message_content_changedTargets = message.content.changedTargets) !== null && _message_content_changedTargets !== void 0 ? _message_content_changedTargets : []);
                }
            },
            streamError: (errorCode)=>onStreamError(resolveErrorMessage(errorCode)),
            draftCreated: (draftId)=>onDraftCreated(draftId),
            reset: ()=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                        activeToolCalls: [],
                        thinkingText: null
                    }))
        });
    };
    const runTurn = async (content)=>{
        if (turnInFlightRef.current) return;
        turnInFlightRef.current = true;
        const controller = new AbortController();
        abortRef.current = controller;
        const userMessage = {
            id: generateUniqueId('msg'),
            role: 'user',
            timestamp: new Date().toISOString(),
            content
        };
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                messages: [
                    ...prev.messages,
                    userMessage
                ],
                isThinking: true,
                error: null
            }));
        let id;
        try {
            id = await ensureConversationId();
        } catch (err) {
            turnInFlightRef.current = false;
            if (controller.signal.aborted) return;
            onStreamError(describeError(err));
            showErrorToast(t.agenticDrafting.errors.create());
            return;
        }
        if (draftId != null && attachedDraftIdRef.current !== draftId) {
            const attachResult = await handleRequest(api.agenticDraft.attachDraft(id, draftId));
            if (controller.signal.aborted) return;
            if (attachResult.success) {
                attachedDraftIdRef.current = draftId;
            } else {
                turnInFlightRef.current = false;
                onStreamError(describeError(attachResult.error));
                showErrorToast(t.agenticDrafting.errors.attachFailed());
                return;
            }
        }
        try {
            await api.agenticDraft.sendMessage({
                conversationId: id,
                content,
                signal: controller.signal,
                onEvent: handleStreamEvent
            });
            onStreamDone();
        } catch (err) {
            if (controller.signal.aborted) return;
            onStreamError(describeError(err));
            showErrorToast(t.agenticDrafting.errors.stream());
        } finally{
            turnInFlightRef.current = false;
        }
    };
    const sendPrompt = (text)=>{
        const attachment = chat.pendingAttachment;
        if (attachment != null) {
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    pendingAttachment: null
                }));
            const trimmed = text.trim();
            return runTurn(_object_spread({
                type: 'pdf-uploaded',
                fileId: attachment.fileId,
                fileName: attachment.fileName
            }, trimmed.length > 0 ? {
                text: trimmed
            } : {}));
        }
        const selection = resolveNumberedListSelection(chat.messages, text);
        if (selection != null) return clickNumberedListOption(selection.messageId, selection.option);
        return runTurn({
            type: 'prompt',
            text
        });
    };
    const removePendingAttachment = ()=>setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                pendingAttachment: null
            }));
    const sendPdfUpload = async (file)=>{
        if (file.size > MAX_FILE_SIZE_BYTES) {
            showErrorToast(t.agenticDrafting.errors.pdfTooLarge());
            return;
        }
        const fileId = generateUniqueId('attachment');
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isUploading: true,
                uploadingFileName: file.name
            }));
        try {
            const { urls } = await api.business.getUploadUrls([
                {
                    id: fileId,
                    displayName: file.name
                }
            ]);
            await uploadFileToCloudStorage(urls[0].url, file, urls[0].headers);
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    pendingAttachment: {
                        fileId,
                        fileName: file.name
                    }
                }));
        } catch (err) {
            Sentry.captureException(err);
            showErrorToast(t.agenticDrafting.errors.pdfUpload());
        } finally{
            setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                    isUploading: false,
                    uploadingFileName: null
                }));
        }
    };
    // List-select item click sends an `apply` userContent with the clicked id
    // as the lone selectedItemId — list-select envelopes are apply targets.
    // The server's apply dispatcher resolves the synthesized patchByItemId to
    // a single setClient / cloneFromTemplate / etc. fragment and applies it
    // directly, saving an LLM turn vs the previous list-select-click flow.
    const clickListSelect = (messageId, itemId, _label)=>runTurn({
            type: 'apply',
            messageId,
            selectedItemIds: [
                itemId
            ]
        });
    const clickNumberedListOption = (messageId, option)=>option.id === SOMETHING_ELSE_OPTION_ID ? runTurn({
            type: 'prompt',
            text: option.label
        }) : runTurn({
            type: 'apply',
            messageId,
            selectedItemIds: [
                option.id
            ]
        });
    const dismissProposal = (messageId)=>runTurn({
            type: 'dismiss',
            messageId
        });
    const applyProposal = (messageId, selectedItemIds)=>runTurn({
            type: 'apply',
            messageId,
            selectedItemIds
        });
    const pickFollowUpSuggestion = (messageId, suggestion)=>applyProposal(messageId, [
            suggestion.id
        ]);
    const refreshConversation = async (conversationId)=>{
        const result = await handleRequest(api.agenticDraft.get(conversationId));
        if (result.success) onHydrate(result.data.messages);
    };
    const undoTurn = async (messageId)=>{
        if (turnInFlightRef.current) return;
        const conversationId = conversationIdRef.current;
        if (conversationId == null) return;
        turnInFlightRef.current = true;
        const controller = new AbortController();
        abortRef.current = controller;
        setChat((prev)=>_object_spread_props(_object_spread({}, prev), {
                isThinking: true,
                error: null
            }));
        try {
            await api.agenticDraft.undo({
                conversationId,
                messageId,
                signal: controller.signal,
                onEvent: handleStreamEvent
            });
            onStreamDone();
        } catch (err) {
            if (controller.signal.aborted) return;
            const status = err.status;
            if (status === 400 || status === 410) {
                onStreamDone();
                showErrorToast(t.agenticDrafting.errors.undoUnavailable());
                await refreshConversation(conversationId);
            } else {
                onStreamError(describeError(err));
                showErrorToast(t.agenticDrafting.errors.stream());
            }
        } finally{
            turnInFlightRef.current = false;
        }
    };
    const submitFeedback = async (messageId, rating, comment)=>{
        const id = conversationIdRef.current;
        if (id == null) return;
        const result = await handleRequest(api.agenticDraft.feedback({
            conversationId: id,
            messageId,
            rating,
            comment
        }));
        if (result.success) showSuccessToast(t.agenticDrafting.feedback.thanks(), AgentDraftingPaneTestIds.FeedbackSuccessToast());
    };
    const dismissFeedback = async (messageId)=>{
        const id = conversationIdRef.current;
        if (id == null) return;
        await api.agenticDraft.dismissFeedback({
            conversationId: id,
            messageId
        }).catch(()=>{});
    };
    const onExpand = ()=>setIsOpen(true);
    const onChevronClick = (e)=>{
        e.stopPropagation();
        setIsOpen(!isOpen);
    };
    const chevronTestId = isOpen ? AgentDraftingPaneTestIds.CollapseChevron() : AgentDraftingPaneTestIds.CollapsedTabChevron();
    const titleTestId = isOpen ? AgentDraftingPaneTestIds.OpenPaneHeaderTitle() : AgentDraftingPaneTestIds.CollapsedTabLabel();
    const paneInnerTestId = isOpen ? AgentDraftingPaneTestIds.OpenPane() : AgentDraftingPaneTestIds.CollapsedTab();
    return /*#__PURE__*/ _jsx(AiGradientShell, {
        className: cn(styles.root, isOpen ? styles.open : styles.collapsed),
        testId: AgentDraftingPaneTestIds.Root(),
        children: /*#__PURE__*/ _jsxs("div", {
            className: styles.paneInner,
            "data-testid": paneInnerTestId,
            onClick: isOpen ? undefined : onExpand,
            role: isOpen ? undefined : 'button',
            tabIndex: isOpen ? undefined : 0,
            children: [
                /*#__PURE__*/ _jsxs("header", {
                    className: cn(styles.header, {
                        [styles.scrolled]: isOpen && isBodyScrolled
                    }),
                    "data-testid": AgentDraftingPaneTestIds.OpenPaneHeader(),
                    children: [
                        /*#__PURE__*/ _jsxs(Typography, {
                            testId: titleTestId,
                            weight: "normal",
                            skin: isOpen ? 'standard' : 'secondary',
                            className: styles.titleTypography,
                            children: [
                                /*#__PURE__*/ _jsx(EditAI, {}),
                                " ",
                                t.agenticDrafting.header.title()
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Badge, {
                            testId: AgentDraftingPaneTestIds.OpenPaneHeaderBeta(),
                            className: styles.betaBadge,
                            "aria-hidden": !isOpen,
                            skin: "primary",
                            rounded: true,
                            outlined: true,
                            children: t.agenticDrafting.header.betaBadge()
                        }),
                        isOpen && /*#__PURE__*/ _jsx(TextButton, {
                            bi: {
                                name: 'clear_agent_drafting_chat'
                            },
                            className: styles.clearChat,
                            skin: "secondary",
                            underline: "none",
                            weight: "normal",
                            variant: "paragraph2",
                            onClick: onClearChat,
                            disabled: isClearDisabled,
                            testId: AgentDraftingPaneTestIds.ClearChatButton(),
                            children: t.agenticDrafting.header.clearChat()
                        }),
                        isOpen && /*#__PURE__*/ _jsx(Divider, {
                            orientation: "vertical",
                            margin: 8,
                            size: 24
                        }),
                        /*#__PURE__*/ _jsx(IconButton, {
                            bi: {
                                name: 'toggle_agent_drafting_pane'
                            },
                            skin: "transparent",
                            className: styles.chevron,
                            testId: chevronTestId,
                            onClick: onChevronClick,
                            "aria-label": isOpen ? 'Collapse' : 'Expand',
                            children: isOpen ? /*#__PURE__*/ _jsx(ChevronRight, {}) : /*#__PURE__*/ _jsx(ChevronLeft, {})
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: styles.body,
                    "aria-hidden": !isOpen,
                    children: /*#__PURE__*/ _jsx(AgentChatOpenState, {
                        chat: chat,
                        isAutoSaving: isAutoSaving,
                        placeholderPool: placeholderPool,
                        onSubmitPrompt: sendPrompt,
                        onPdfUpload: sendPdfUpload,
                        onRemoveAttachment: removePendingAttachment,
                        onDismissProposal: dismissProposal,
                        onApplyProposal: applyProposal,
                        onListSelectClick: clickListSelect,
                        onNumberedListClick: clickNumberedListOption,
                        onPickFollowUpSuggestion: pickFollowUpSuggestion,
                        onSubmitFeedback: submitFeedback,
                        onDismissFeedback: dismissFeedback,
                        onUndoProposal: undoTurn,
                        onScrolledChange: setIsBodyScrolled,
                        testId: AgentDraftingPaneTestIds.OpenStateContent()
                    })
                })
            ]
        })
    });
};
